import { Brain, Cpu, Eye, MessageSquare, FileSearch, Shield, Bot, Users, Zap, Search } from 'lucide-react';
import type { Product } from './types';

export const aiMachineLearningProducts: Product[] = [
  {
    id: 'abacus-ai-platform',
    title: 'Abacus AI',
    subtitle: 'Automated Deep Learning Platform',
    description: 'End-to-end AI solutions for building, deploying, and managing ML models with fully automated deep learning capabilities and enterprise-grade security for UK organisations.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#ai', '#machine-learning', '#deep-learning', '#automation', '#enterprise'],
    features: [
      'Automated model creation and deployment',
      'End-to-end ML pipeline management',
      'Real-time model monitoring and optimisation',
      'Enterprise security and UK data compliance',
      'No-code AI development interface',
      'Scalable cloud infrastructure'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'ai-customer-service-automation',
    title: 'AI Customer Service Automation',
    subtitle: 'Intelligent Support Platform',
    description: 'Advanced NLP-powered customer service automation that handles complex inquiries with human-like responses and reduces support costs by 60% for UK businesses.',
    icon: MessageSquare,
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#customer-service', '#ai', '#automation', '#support', '#enterprise'],
    features: [
      'Natural language understanding and response',
      'Multi-channel support integration',
      'Automated ticket routing and prioritization',
      'Sentiment analysis and escalation',
      'Custom knowledge base training',
      'Performance analytics and reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'predictive-lead-scoring',
    title: 'Predictive Lead Scoring Platform',
    subtitle: 'ML-Powered Sales Intelligence',
    description: 'Machine learning algorithms analyse customer data to identify high-value leads with 85% accuracy, increasing conversion rates significantly for UK sales teams.',
    icon: Search,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#sales', '#lead-generation', '#predictive-analytics', '#crm', '#marketing'],
    features: [
      'AI-powered lead scoring algorithms',
      'Behavioral pattern analysis',
      'Integration with popular CRM systems',
      'Real-time score updates',
      'Custom scoring model training',
      'ROI tracking and optimisation'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'intelligent-document-processing',
    title: 'Intelligent Document Processing',
    subtitle: 'AI Document Automation',
    description: 'Automated document extraction, classification, and processing using computer vision and NLP to eliminate manual data entry tasks.',
    icon: FileSearch,
    image: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#document-management', '#automation', '#ai', '#compliance', '#finance'],
    features: [
      'Optical character recognition (OCR)',
      'Intelligent document classification',
      'Data extraction and validation',
      'Workflow automation integration',
      'Audit trail and compliance tracking',
      'Multi-language support'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'ai-content-creation-platform',
    title: 'AI Content Creation Platform',
    subtitle: 'Generative Content Engine',
    description: 'Advanced generative AI creates marketing copy, blog posts, and social media content that matches your brand voice and drives engagement.',
    icon: Bot,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#content-creation', '#marketing', '#ai', '#copywriting', '#social-media'],
    features: [
      'Brand voice training and consistency',
      'Multi-format content generation',
      'SEO optimisation suggestions',
      'Plagiarism detection and originality scoring',
      'Content performance analytics',
      'Team collaboration and approval workflows'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'computer-vision-inspection',
    title: 'Computer Vision Inspection System',
    subtitle: 'AI Quality Control',
    description: 'Advanced computer vision technology for automated quality control and defect detection in manufacturing with 99.5% accuracy rates.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#manufacturing', '#quality-control', '#computer-vision', '#automation', '#inspection'],
    features: [
      'Real-time defect detection',
      'Custom model training for specific products',
      'Integration with production lines',
      'Quality metrics and reporting',
      'Threshold customisation and alerts',
      'Historical trend analysis'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-teal-500 to-blue-500'
  },
  {
    id: 'conversational-ai-platform',
    title: 'Conversational AI Platform',
    subtitle: 'Advanced Chatbot Framework',
    description: 'Next-generation conversational AI that understands context, maintains memory, and provides human-like interactions across all channels.',
    icon: MessageSquare,
    image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#chatbots', '#conversational-ai', '#customer-support', '#automation', '#nlp'],
    features: [
      'Context-aware conversation management',
      'Multi-turn dialogue capabilities',
      'Voice and text integration',
      'Emotional intelligence and sentiment detection',
      'Multilingual conversation support',
      'Analytics and conversation insights'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 'ai-recruitment-matching',
    title: 'AI Recruitment Matching',
    subtitle: 'Intelligent Talent Acquisition',
    description: 'Machine learning-powered recruitment platform that matches candidates with positions based on skills, experience, and cultural fit indicators.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#recruitment', '#hr', '#talent-acquisition', '#ai-matching', '#hiring'],
    features: [
      'AI-powered candidate matching',
      'Skills gap analysis and recommendations',
      'Cultural fit assessment algorithms',
      'Automated candidate screening',
      'Bias reduction and diversity optimisation',
      'Predictive hiring success modeling'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-rose-500 to-pink-500'
  },
  {
    id: 'intelligent-process-automation',
    title: 'Intelligent Process Automation',
    subtitle: 'RPA with AI Decision Making',
    description: 'Combines robotic process automation with AI to handle complex business processes requiring decision-making and adaptation.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#process-automation', '#rpa', '#ai', '#workflow', '#enterprise'],
    features: [
      'Intelligent decision-making capabilities',
      'Complex workflow automation',
      'Exception handling and adaptation',
      'Process optimisation recommendations',
      'Integration with enterprise systems',
      'Performance monitoring and analytics'
    ],
    pricing: { setup: 51200, monthly: 2355 },
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'ai-cybersecurity-threat-detection',
    title: 'AI Cybersecurity Threat Detection',
    subtitle: 'Predictive Security Platform',
    description: 'Advanced machine learning algorithms identify anomalous patterns and predict potential security threats before they materialize.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
    category: 'AI & Machine Learning',
    status: 'Available',
    businessTypes: ['#cybersecurity', '#threat-detection', '#ai', '#security-analytics', '#enterprise'],
    features: [
      'Real-time threat detection and alerting',
      'Behavioral analysis and anomaly detection',
      'Predictive threat modeling',
      'Automated incident response',
      'Threat intelligence integration',
      'Compliance reporting and audit trails'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-red-500 to-pink-500'
  }
];